import React, { useCallback, useEffect, useState } from 'react';
import { Eye, LoaderCircle, PencilLine, Plus, Trash2, UsersRound } from 'lucide-react';
import {
  createProjectUserApi,
  deleteProjectUserApi,
  fetchProjectUsersApi,
  updateProjectUserApi
} from '../../api/usersAdmin.api';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { useI18n } from '../../hooks/useI18n';
import { formatJalaliDateTime } from '../../utils/jalaliDate';

const buildUserForm = (user = null) => ({
  fullName: user?.fullName || '',
  username: user?.username || '',
  password: ''
});

const getUserInitials = (user) => {
  const source = user?.fullName || user?.username || '';
  const parts = source.trim().split(/\s+/).filter(Boolean);

  if (!parts.length) {
    return 'U';
  }

  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
};

export const UsersPanel = ({ projectId, forceLoading = false, disableFetch = false }) => {
  const { t, language } = useI18n();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [activeUser, setActiveUser] = useState(null);
  const [form, setForm] = useState(() => buildUserForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailsUser, setDetailsUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionMessage, setActionMessage] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const items = await fetchProjectUsersApi(projectId);
      setUsers(items);
    } catch (loadError) {
      setUsers([]);
      setError(loadError?.message || t('usersLoadError'));
    } finally {
      setLoading(false);
    }
  }, [projectId, t]);

  useEffect(() => {
    if (!projectId || disableFetch) {
      setLoading(Boolean(projectId && disableFetch));
      return;
    }

    loadUsers();
  }, [disableFetch, loadUsers, projectId]);

  const openCreateModal = () => {
    setActiveUser(null);
    setForm(buildUserForm());
    setFormError('');
    setActionMessage('');
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    setActiveUser(user);
    setForm(buildUserForm(user));
    setFormError('');
    setActionMessage('');
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) {
      return;
    }

    setModalOpen(false);
    setActiveUser(null);
    setForm(buildUserForm());
    setFormError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (saving) {
      return;
    }

    if ((!activeUser || form.password) && form.password.length < 6) {
      setFormError(t('usersPasswordMinLength'));
      return;
    }

    setSaving(true);
    setFormError('');

    const payload = {
      projectId,
      fullName: form.fullName.trim(),
      username: activeUser ? (form.username.trim() || null) : form.username.trim(),
      password: activeUser ? (form.password || null) : form.password,
      additionalFields: activeUser?.additionalFields || {}
    };

    try {
      if (activeUser) {
        await updateProjectUserApi({ ...payload, userId: activeUser.id });
        setActionMessage(t('usersUpdateSuccess'));
      } else {
        await createProjectUserApi(payload);
        setActionMessage(t('usersCreateSuccess'));
      }

      await loadUsers();
      window.setTimeout(closeModal, 450);
    } catch (saveError) {
      setFormError(saveError?.message || (activeUser ? t('usersUpdateError') : t('usersCreateError')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) {
      return;
    }

    setDeleting(true);
    setActionMessage('');
    setError('');

    try {
      await deleteProjectUserApi({ projectId, userId: deleteTarget.id });
      setDeleteTarget(null);
      setActionMessage(t('usersDeleteSuccess'));
      await loadUsers();
    } catch (deleteError) {
      setError(deleteError?.message || t('usersDeleteError'));
    } finally {
      setDeleting(false);
    }
  };

  if (forceLoading || loading) {
    return (
      <section className="space-y-5">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-950 dark:text-white">{t('usersTitle')}</h1>
          <p className="mt-1 text-sm text-neutral-500">{t('usersSubtitle')}</p>
        </div>
        <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white p-8 dark:border-neutral-800 dark:bg-neutral-950">
          <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 text-neutral-500">
            <LoaderCircle className="animate-spin" size={20} />
            <span className="text-sm">{t('usersLoading')}</span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-neutral-950 dark:text-white">{t('usersTitle')}</h1>
            <p className="mt-1 text-sm text-neutral-500">{t('usersSubtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-500 dark:border-neutral-800 dark:bg-neutral-950">
              {users.length} {t('usersPanel')}
            </span>
            <Button type="button" onClick={openCreateModal}>
              <Plus size={16} />
              {t('usersCreate')}
            </Button>
          </div>
        </div>

        {actionMessage ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-200">
            {actionMessage}
          </div>
        ) : null}

        {error ? (
          <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-10 text-center dark:border-rose-900/40 dark:bg-rose-900/20">
            <p className="text-sm text-rose-700 dark:text-rose-200">{error}</p>
            <Button type="button" variant="secondary" onClick={loadUsers}>
              {t('requestsRetry')}
            </Button>
          </div>
        ) : !users.length ? (
          <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-neutral-200 px-6 py-14 text-center dark:border-neutral-800">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-neutral-200 bg-neutral-50 text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900">
              <UsersRound size={20} />
            </div>
            <div className="max-w-md space-y-2">
              <h2 className="text-sm font-semibold text-neutral-950 dark:text-white">{t('usersEmptyTitle')}</h2>
              <p className="text-sm leading-6 text-neutral-500">{t('usersEmptyBody')}</p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid gap-3 lg:hidden">
              {users.map((user) => (
                <article
                  key={user.id}
                  className="rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950"
                >
                  <div className="flex items-start gap-3">
                    <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-sm font-semibold text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                      {getUserInitials(user)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="truncate text-sm font-semibold text-neutral-950 dark:text-white">{user.fullName || '—'}</h2>
                      <p className="mt-0.5 truncate text-sm text-neutral-500">{user.username || '—'}</p>
                      <p className="mt-2 text-xs text-neutral-400">{formatJalaliDateTime(user.updatedAt || user.createdAt, language)}</p>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-end gap-1">
                    <button
                      type="button"
                      className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-neutral-600 transition hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-900"
                      aria-label={t('usersView')}
                      onClick={() => setDetailsUser(user)}
                    >
                      <Eye size={14} />
                      {t('usersView')}
                    </button>
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-neutral-800 dark:hover:text-white"
                      aria-label={t('usersEdit')}
                      onClick={() => openEditModal(user)}
                    >
                      <PencilLine size={15} />
                    </button>
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10 dark:hover:text-rose-300"
                      aria-label={t('usersDelete')}
                      onClick={() => setDeleteTarget(user)}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </article>
              ))}
            </div>

            <div className="hidden overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-800 lg:block">
              <table className="w-full text-start text-sm">
                <thead className="bg-neutral-50 text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-400 dark:bg-neutral-900">
                  <tr>
                    <th className="px-4 py-3 text-start">{t('usersFullName')}</th>
                    <th className="px-4 py-3 text-start">{t('usersUsername')}</th>
                    <th className="px-4 py-3 text-start">{t('updatedAt')}</th>
                    <th className="px-4 py-3 text-end">{t('requestsActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr
                      key={user.id}
                      className="border-t border-neutral-100 transition hover:bg-neutral-50 dark:border-neutral-900 dark:hover:bg-neutral-900/60"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-xs font-semibold text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                            {getUserInitials(user)}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-medium text-neutral-950 dark:text-white">{user.fullName || '—'}</p>
                            <p className="mt-0.5 max-w-[220px] truncate text-[11px] text-neutral-400">{user.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-neutral-500">{user.username || '—'}</td>
                      <td className="px-4 py-3 text-neutral-500">
                        {formatJalaliDateTime(user.updatedAt || user.createdAt, language)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-neutral-600 transition hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
                            aria-label={t('usersView')}
                            onClick={() => setDetailsUser(user)}
                          >
                            <Eye size={14} />
                            {t('usersView')}
                          </button>
                          <button
                            type="button"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-neutral-800 dark:hover:text-white"
                            aria-label={t('usersEdit')}
                            onClick={() => openEditModal(user)}
                          >
                            <PencilLine size={15} />
                          </button>
                          <button
                            type="button"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10 dark:hover:text-rose-300"
                            aria-label={t('usersDelete')}
                            onClick={() => setDeleteTarget(user)}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={activeUser ? t('usersEditTitle') : t('usersCreateTitle')}
        closeDisabled={saving}
        panelClassName="flex max-h-[min(90vh,680px)] max-w-lg flex-col overflow-hidden p-0"
        contentClassName="mb-0 shrink-0 border-b border-neutral-100 px-4 py-3 dark:border-neutral-900 sm:px-5"
        titleClassName="text-start text-base"
      >
        <form className="flex min-h-0 flex-1 flex-col text-start" onSubmit={handleSubmit}>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-2.5 sm:px-5">
            <p className="text-xs leading-6 text-neutral-500">{t('usersFormHint')}</p>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Input
                  label={t('usersFullName')}
                  value={form.fullName}
                  disabled={saving}
                  required
                  className="rounded-xl px-3 py-2.5"
                  onChange={(event) => setForm((current) => ({ ...current, fullName: event.target.value }))}
                />
              </div>
              <Input
                label={t('usersUsername')}
                value={form.username}
                disabled={saving}
                required={!activeUser}
                placeholder={activeUser ? t('usersLeaveBlankUnchanged') : ''}
                className="rounded-xl px-3 py-2.5"
                onChange={(event) => setForm((current) => ({ ...current, username: event.target.value }))}
              />
              <Input
                label={t('usersPassword')}
                type="password"
                value={form.password}
                disabled={saving}
                required={!activeUser}
                minLength={6}
                placeholder={activeUser ? t('usersLeaveBlankUnchanged') : ''}
                className="rounded-xl px-3 py-2.5"
                onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
              />
            </div>

            {activeUser ? (
              <p className="rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs leading-6 text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900">
                {t('usersPasswordHint')}
              </p>
            ) : null}

            {formError ? (
              <p className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200">
                {formError}
              </p>
            ) : null}
            {actionMessage ? (
              <p className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
                {actionMessage}
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-neutral-100 px-4 py-3 dark:border-neutral-900 sm:px-5">
            <Button type="button" variant="secondary" onClick={closeModal} disabled={saving}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? t('usersSaving') : t('save')}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={Boolean(detailsUser)}
        onClose={() => setDetailsUser(null)}
        title={t('usersDetailsTitle')}
        panelClassName="flex max-h-[min(88vh,640px)] max-w-xl flex-col overflow-hidden p-0"
        contentClassName="mb-0 shrink-0 border-b border-neutral-100 px-4 py-3 dark:border-neutral-900 sm:px-5"
        titleClassName="text-start text-base"
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2.5 text-start sm:px-5">
          <div className="rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
            <div className="flex items-start gap-3">
              <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-sm font-semibold text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                {getUserInitials(detailsUser)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-base font-semibold text-neutral-950 dark:text-white">{detailsUser?.fullName || '—'}</p>
                <p className="mt-1 break-all text-sm text-neutral-500">{detailsUser?.username || '—'}</p>
              </div>
            </div>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
              <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-400">ID</p>
              <p className="mt-1 break-all text-sm font-medium text-neutral-950 dark:text-white">{detailsUser?.id || '—'}</p>
            </div>
            <div className="rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
              <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-400">{t('updatedAt')}</p>
              <p className="mt-1 text-sm font-medium text-neutral-950 dark:text-white">
                {formatJalaliDateTime(detailsUser?.updatedAt || detailsUser?.createdAt, language)}
              </p>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 justify-end border-t border-neutral-100 px-4 py-3 dark:border-neutral-900 sm:px-5">
          <Button type="button" variant="secondary" onClick={() => setDetailsUser(null)}>
            {t('close')}
          </Button>
        </div>
      </Modal>

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleting) {
            setDeleteTarget(null);
          }
        }}
        title={t('usersDeleteTitle')}
        closeDisabled={deleting}
        contentClassName="mb-3"
      >
        <div className="space-y-3 text-start">
          <p className="text-sm text-neutral-500">
            {t('usersDeleteConfirm', { name: deleteTarget?.fullName || deleteTarget?.username || '' })}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t('cancel')}
            </Button>
            <Button type="button" variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? t('usersDeleting') : t('delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
